import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { isNil } from 'lodash';
import STRING_KEYS from '../../../../language/keys';
import { IntercomManager } from '../../../../services/intercom-manager';
import {
  Description, StyledDivider, Subtitle, TextContainer, Title,
} from '../ats-initialization.styled';
import { PerfectButtonHoverVariants, PerfectButtonSize, PerfectButtonVariants } from '../../../Common/Buttons/perfect-base-button/perfect-base-button.enums';
import { useGetMyUserQuery } from '../../../../store/rtk-query/apis/user/user.toolkit-api';
import { accountEndpoints } from '../../../../store/rtk-query/apis/account/account.toolkit-api';
import { atsIntegrationEndpoints } from '../../../../store/rtk-query/apis/ats-integration/ats-integration.toolkit-api';
import {
  BackButton, BackButtonIcon, ButtonsContainer, ConfirmButton, ContactCsLink, Container,
} from './login-with-ats.styled';
import { LoginWithAtsProps, OAuthLoginCompletedEventPayLoad } from './login-with-ats';
import { OAUTH_EVENT_IDENTIFIER, PERMISSION_ERROR_MESSAGE, OATH_POPUP_DIMENSIONS } from './login-with-ats-consts';

export const LoginWithATS = ({
  integrationType,
  isCreateCustomAtsIntegrationLoading,
  backButtonClickHandler,
  onLoginSuccess,
}: LoginWithAtsProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const {
    data: { accountId } = {},
  } = useGetMyUserQuery();
  const dispatch = useDispatch();

  const {
    data,
    isLoading: isGetAtsLoginUrlLoading,
    isError: isGetAtsLoginUrlError,
  } = atsIntegrationEndpoints.useGetAtsLoginUrlQuery(integrationType, { skip: isNil(integrationType) });

  const translatedIntegrationName = useMemo(
    () => translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.CUSTOM_INTEGRATION_NAMES[integrationType]),
    [integrationType, translate],
  );

  const onContactCsButtonClick = () => {
    IntercomManager.showChat();
  };

  const onOpenWindowButtonClick = () => {
    const top = window.top?.outerHeight ?
      (window.top.outerHeight - OATH_POPUP_DIMENSIONS.HEIGHT) / 2 :
      100;
    const left = window.top?.outerWidth ?
      (window.top.outerWidth - OATH_POPUP_DIMENSIONS.WIDTH) / 2 :
      100;

    popupRef.current = window.open(
      data?.url,
      'OAuth',
      `width=${OATH_POPUP_DIMENSIONS.WIDTH},height=${OATH_POPUP_DIMENSIONS.HEIGHT},top=${top},left=${left}`,
    );

    if (!popupRef.current) {
      setError('Popup blocked. Please allow popups in your browser.');

      return;
    }

    setIsConnecting(true);
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent<OAuthLoginCompletedEventPayLoad>) => {
      // Check if the event is the success message of integration creation
      if (event.data.identifier === OAUTH_EVENT_IDENTIFIER) {
        const {
          success, provider, atsCreated,
        } = event.data;

        if (provider === integrationType && success) {
          dispatch(accountEndpoints.util.updateQueryData('getAccountById', accountId, (account) => {
            account.atsIntegration = atsCreated;
          }));
          onLoginSuccess();
        } else if (!success) {
          if (event.data.error === PERMISSION_ERROR_MESSAGE) {
            setError(translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.PERMISSION_ERROR));
          } else {
            setError(translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.GENERAL_ERROR));
          }
        }
        setIsConnecting(false);
        popupRef.current?.close();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [integrationType, accountId, onLoginSuccess, dispatch, translate]);

  return (
    <Container>
      <TextContainer>
        <Title>{translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.TITLE, { atsName: translatedIntegrationName })}</Title>
        <Description>
          <Trans i18nKey={ STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.DESCRIPTION } values={ { atsName: translatedIntegrationName } }>
            <ContactCsLink onClick={ onContactCsButtonClick } />
          </Trans>
        </Description>
        <StyledDivider />
        <Subtitle>{translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.BUTTON_DESCRIPTION)}</Subtitle>
      </TextContainer>
      {error && <p style={ { color: 'red' } }>{error}</p>}
      <ButtonsContainer>
        <BackButton
          StartIcon={ BackButtonIcon }
          variant={ PerfectButtonVariants.Outline }
          hoverVariant={ PerfectButtonHoverVariants.Grow }
          size={ PerfectButtonSize.Large }
          onClick={ backButtonClickHandler }
          disabled={ isCreateCustomAtsIntegrationLoading }
        />
        <ConfirmButton
          onClick={ () => onOpenWindowButtonClick() }
          size={ PerfectButtonSize.Large }
          hoverVariant={ PerfectButtonHoverVariants.Grow }
          disabled={ isCreateCustomAtsIntegrationLoading || isGetAtsLoginUrlLoading || isConnecting || isGetAtsLoginUrlError }
          isLoading={ isCreateCustomAtsIntegrationLoading || isGetAtsLoginUrlLoading }
        >
          {isConnecting ?
            translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.OPEN_WINDOW_BUTTON_LOADING) :
            translate(STRING_KEYS.ATS_INITIALIZATION_DIALOG.AUTHENTICATE_WITH_LOGIN.OPEN_WINDOW_BUTTON)}
        </ConfirmButton>
      </ButtonsContainer>
    </Container>
  );
};
